import React, { useState, useEffect, useRef } from "react";
import {
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableDataCell,
  CTableBody,
  CFormCheck,
  CModal,
  CModalBody,
  CModalHeader,
  CModalTitle,
  CModalFooter,
  CButton,
  CPagination,
  CPaginationItem,
  CAlert,
  CSpinner,
} from "@coreui/react";
import { Resizable } from "react-resizable";
import axios from "axios";
import InformationObject from "../InformationObject/InformationObject";
import "./Table.scss";
import CIcon from "@coreui/icons-react";
import { cilSettings, cilWarning } from "@coreui/icons";
import "react-resizable/css/styles.css";
import Loading from "../Loading/Loading";

const TABLE_STORAGE_KEY = "table_columns_state";

const ResizableHeader = ({ onResize, width, children }) => {
  return (
    <CTableHeaderCell
      scope="col"
      style={{
        width: width, // Применяем ширину к самой ячейке
        position: "relative", // Важно для позиционирования handle
        padding: 0, // Убираем паддинги ячейки, чтобы Resizable занял всё место
      }}
    >
      <Resizable
        width={width}
        height={0}
        onResize={onResize}
        draggableOpts={{ enableUserSelectHack: false }}
        // Можно явно передать кастомный handle, если стандартный не создается
        handle={
          <span
            className="react-resizable-handle"
            onClick={(e) => e.stopPropagation()}
          />
        }
      >
        {/* Внутренний контейнер для контента, чтобы вернуть паддинги */}
        <div style={{ padding: "10px 12px", height: "100%" }}>
          {children}
        </div>
      </Resizable>
    </CTableHeaderCell>
  );
};

export const Table = ({ customerId, filter, refreshTrigger }) => {
  const [columns, setColumns] = useState(() => {
    const savedColumns = localStorage.getItem(TABLE_STORAGE_KEY);
    return savedColumns
      ? JSON.parse(savedColumns)
      : [
          {
            key: "lineListId",
            label: "ID строки",
            width: 100,
            isVisible: false,
          },
          { key: "ldoc", label: "№ заключения", width: 150, isVisible: true },
          { key: "region", label: "Регион", width: 120, isVisible: true },
          {
            key: "name",
            label: "Название объекта",
            width: 150,
            isVisible: true,
          },
          {
            key: "characteristics",
            label: "Характеристики объекта",
            width: 250,
            isVisible: true,
          },
          {
            key: "workName",
            label: "Наименование работы",
            width: 200,
            isVisible: true,
          },
          {
            key: "workStatus",
            label: "Статус работы",
            width: 150,
            isVisible: true,
          },
          {
            key: "address",
            label: "Адрес объекта",
            width: 200,
            isVisible: true,
          },
          {
            key: "inDirectory",
            label: "В справочнике",
            width: 174.28,
            isVisible: false,
          },
          {
            key: "year",
            label: "Год проведения работ",
            width: 189.58,
            isVisible: true,
          },
        ];
  });

  const [filters, setFilters] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [rowLoading, setRowLoading] = useState(false);
  const [rowError, setRowError] = useState(null);
  const rowsPerPage = 200;

  const tableRef = useRef();
  const token = localStorage.getItem("token");

  const refreshData = () => {
    if (customerId) {
      setLoading(true);
      axios
        .get(`/api/customer/${customerId}/details/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          const fetchedData = response.data;
          setOriginalData(fetchedData);
          setData(fetchedData);
          setLoading(false);
        })
        .catch((error) => {
          setOriginalData([]);
          setData([]);
          setLoading(false);
        });
    } else if (filter) {
      setLoading(true);
      axios
        .get(`/api/customer/filtered/${encodeURIComponent(filter)}/`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        .then((response) => {
          const fetchedData = response.data;
          setOriginalData(fetchedData);
          setData(fetchedData);
          setLoading(false);
        })
        .catch((error) => {
          setOriginalData([]);
          setData([]);
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    refreshData();
  }, [customerId, filter, refreshTrigger]);

  useEffect(() => {
    if (!originalData.length) return;

    let filteredData = [...originalData];

    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        const columnIndex = columns.findIndex((col) => col.key === key);
        filteredData = filteredData.filter((item) => {
          const cellValue = String(item[columnIndex] ?? "").toLowerCase();
          const result = cellValue.includes(value.toLowerCase());
          return result;
        });
      }
    });

    if (sortConfig.key) {
      const sortColumnIndex = columns.findIndex(
        (col) => col.key === sortConfig.key,
      );
      if (sortColumnIndex !== -1) {
        filteredData.sort((a, b) => {
          const aValue = a[sortColumnIndex];
          const bValue = b[sortColumnIndex];
          if (typeof aValue === "number" && typeof bValue === "number") {
            return sortConfig.direction === "asc"
              ? aValue - bValue
              : bValue - aValue;
          }
          if (aValue instanceof Date && bValue instanceof Date) {
            return sortConfig.direction === "asc"
              ? aValue.getTime() - bValue.getTime()
              : bValue.getTime() - aValue.getTime();
          }
          return sortConfig.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        });
      }
    }

    setData(filteredData);
  }, [filters, originalData, sortConfig, columns]);

  const handleResize =
    (key) =>
    (e, { size }) => {
      const newColumns = columns.map((col) =>
        col.key === key ? { ...col, width: size.width } : col,
      );
      setColumns(newColumns);
      localStorage.setItem(TABLE_STORAGE_KEY, JSON.stringify(newColumns));
    };

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleFilterChange = (key) => (e) => {
    const value = e.target.value;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [key]: value,
    }));
  };

  const handleRowClick = async (item) => {
    const lineListId = item[0];
    const ldoc = item[1];
    const region = item[2];
    const name = item[3];
    const characteristics = item[4];
    const workName = item[5];
    const workStatus = item[6];
    const address = item[7];
    const inDirectory = item[8];
    const year = item[9];

    const tableObject = {
      LineListID: lineListId,
      LDoc: ldoc,
      region: region,
      name: name,
      characteristics: characteristics,
      workName: workName,
      workStatus: workStatus,
      address: address,
      inDirectory: inDirectory,
      year: year,
    };

    setSelectedRow(tableObject);
    setIsModalOpen(true);
    setRowLoading(true);
    setRowError(null);

    try {
      const response = await axios.get(`/api/ldoc/${ldoc}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        timeout: 10000,
      });

      const fullObject = {
        ...tableObject,
        ...response.data,
      };

      setSelectedRow(fullObject);
      setRowLoading(false);
    } catch (error) {
      if (error.response?.status === 401) {
        setRowError(
          "Ошибка авторизации. У вас нет доступа к детальной информации об объекте.",
        );
      } else if (error.response?.status === 403) {
        setRowError(
          "Доступ запрещен. У вас нет прав для просмотра деталей этого объекта.",
        );
      } else if (error.response?.status === 404) {
        setRowError("Информация об объекте не найдена.");
      } else if (error.message.includes("timeout")) {
        setRowError("Превышено время ожидания ответа от сервера.");
      } else if (!error.response) {
        setRowError(
          "Нет подключения к серверу. Проверьте интернет-соединение.",
        );
      } else {
        setRowError(
          `Ошибка сервера: ${error.response?.status || "Неизвестная ошибка"}.`,
        );
      }

      setRowLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setRowError(null);
    setRowLoading(false);
    refreshData();
  };

  const totalPages = Math.ceil(data.length / rowsPerPage);
  const paginatedData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const visibleColumns = columns.filter(
    (col) => col.isVisible && col.key !== "lineListId",
  );

  return (
    <div>
      {loading && <Loading name="Загрузка данных" />}
      <CButton
        onClick={() => setIsSettingsModalOpen(true)}
        className="setting-table"
      >
        <CIcon icon={cilSettings} />
      </CButton>
      <CTable className="about-objects__table" ref={tableRef} responsive>
        <CTableHead className="about-objects__head">
          <CTableRow>
            {visibleColumns.map((col) => (
              <ResizableHeader
                key={col.key}
                width={col.width}
                onResize={handleResize(col.key)}
              >
                {/* Весь контент теперь внутри */}
                <input
                  type="text"
                  className="searchcolumn"
                  value={filters[col.key] || ""}
                  onChange={handleFilterChange(col.key)}
                  onClick={(e) => e.stopPropagation()} // Чтобы клик по инпуту не сортировал
                />
                <p 
                  onClick={() => handleSort(col.key)} 
                  style={{ cursor: "pointer", margin: 0 }} // Добавляем стили сюда
                >
                  {col.label}
                  {sortConfig.key === col.key && (
                    <span className="ms-2">
                      {sortConfig.direction === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </p>
              </ResizableHeader>
            ))}
          </CTableRow>
        </CTableHead>
        <CTableBody className="about-objects__body">
          {paginatedData.map((originalItem, rowIndex) => {
            const item = originalItem.slice(1);
            return (
              <CTableRow
                key={rowIndex}
                onClick={() => handleRowClick(originalItem)}
                style={{ cursor: "pointer" }}
              >
                {visibleColumns.map((col) => {
                  const dataIndex = columns.findIndex((c) => c.key === col.key);
                  const displayIndex = dataIndex - 1;
                  return (
                    <CTableDataCell key={col.key} style={{ width: col.width }}>
                      {col.key === "inDirectory" ? (
                        <CFormCheck
                          defaultChecked={item[displayIndex]}
                          disabled
                        />
                      ) : (
                        item[displayIndex]
                      )}
                    </CTableDataCell>
                  );
                })}
              </CTableRow>
            );
          })}
        </CTableBody>
      </CTable>

      {totalPages > 1 && (
        <CPagination
          className="pagination-table-line"
          size="sm"
          aria-label="Page navigation example"
        >
          <CPaginationItem
            disabled={currentPage === 1}
            onClick={() => handlePageChange(currentPage - 1)}
          >
            Назад
          </CPaginationItem>
          {Array.from({ length: totalPages }, (_, i) => (
            <CPaginationItem
              key={i + 1}
              active={currentPage === i + 1}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </CPaginationItem>
          ))}
          <CPaginationItem
            disabled={currentPage === totalPages}
            onClick={() => handlePageChange(currentPage + 1)}
          >
            Вперед
          </CPaginationItem>
        </CPagination>
      )}

      <CModal
        className="information-object-modal"
        visible={isModalOpen}
        onClose={handleCloseModal}
        size="xl"
        backdrop={rowLoading ? false : true}
      >
        <CModalHeader>
          <CModalTitle>
            Информация об объекте
            {rowLoading && <span className="ms-2">(загрузка...)</span>}
          </CModalTitle>
        </CModalHeader>
        <CModalBody>
          {rowError && (
            <CAlert color="warning" className="mb-3">
              <CIcon icon={cilWarning} className="me-2" />
              {rowError}
              <div className="mt-2">
                <small className="text-muted">
                  Отображается базовая информация из таблицы.
                </small>
              </div>
            </CAlert>
          )}

          {rowLoading && !selectedRow && (
            <div className="text-center py-4">
              <CSpinner color="primary" />
              <p className="mt-3">Загрузка деталей объекта...</p>
            </div>
          )}

          {selectedRow && (
            <InformationObject
              object={selectedRow}
              onClose={handleCloseModal}
            />
          )}
        </CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={handleCloseModal}>
            Закрыть
          </CButton>
        </CModalFooter>
      </CModal>

      <CModal
        visible={isSettingsModalOpen}
        onClose={() => setIsSettingsModalOpen(false)}
      >
        <CModalHeader>
          <CModalTitle>Настройка столбцов</CModalTitle>
        </CModalHeader>
        <CModalBody>
          {columns.map((col) => (
            <div key={col.key}>
              <CFormCheck
                label={col.label}
                checked={col.isVisible}
                onChange={(e) => {
                  const newColumns = columns.map((c) =>
                    c.key === col.key
                      ? { ...c, isVisible: e.target.checked }
                      : c,
                  );
                  setColumns(newColumns);
                  localStorage.setItem(
                    TABLE_STORAGE_KEY,
                    JSON.stringify(newColumns),
                  );
                }}
              />
            </div>
          ))}
        </CModalBody>
      </CModal>
    </div>
  );
};

export default Table;
